import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { inmateService } from '../../../services/inmateService';

const FlexibleSearchPanel = ({ onSearch, loading }) => {
  const [searchForm, setSearchForm] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    facility: '',
    agency: '',
    status: '',
    arrestDateFrom: '',
    arrestDateTo: '',
    releaseDateFrom: '',
    releaseDateTo: '',
    chargeQuery: '',
    warrantQuery: '',
    city: '',
    zip: '',
    bookingNumber: '',
    caseNumber: '',
    fuzzy: false
  });

  const [facilities, setFacilities] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);

  // Load facilities and agencies
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [facilitiesResult, agenciesResult] = await Promise.all([
          inmateService?.getFacilities?.(),
          inmateService?.getAgencies?.()
        ]);

        if (facilitiesResult?.success) {
          setFacilities(facilitiesResult?.data?.map(f => ({
            value: f?.facility_id,
            label: f?.facility_name
          })));
        }

        if (agenciesResult?.success) {
          setAgencies(agenciesResult?.data?.map(a => ({
            value: a?.agency_code,
            label: a?.agency_name
          })));
        }
      } catch (error) {
        console.warn('Failed to load search options:', error);
      }
    };

    loadOptions();
  }, []);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'IN_CUSTODY', label: 'In Custody' },
    { value: 'RELEASED', label: 'Released' },
    { value: 'DISMISSED', label: 'Dismissed' },
    { value: 'UNKNOWN', label: 'Unknown' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setSearchForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setSearchForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    
    // Check if at least one field is filled
    const hasSearchTerms = Object.entries(searchForm)?.some(([key, value]) => {
      if (key === 'fuzzy') return false; // Skip fuzzy flag
      return value?.toString()?.trim() !== '';
    });
    
    if (!hasSearchTerms) {
      alert('Please enter at least one search field');
      return;
    }

    onSearch?.(searchForm);
  };

  const handleClear = () => {
    setSearchForm({
      firstName: '',
      lastName: '',
      dob: '',
      facility: '',
      agency: '',
      status: '',
      arrestDateFrom: '',
      arrestDateTo: '',
      releaseDateFrom: '',
      releaseDateTo: '',
      chargeQuery: '',
      warrantQuery: '',
      city: '',
      zip: '',
      bookingNumber: '',
      caseNumber: '',
      fuzzy: false
    });
  };

  const toggleAdvancedMode = () => {
    setAdvancedMode(!advancedMode);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
            <Icon name="Search" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Flexible Inmate Search</h3>
            <p className="text-sm text-muted-foreground">
              Search by any field or combination - includes both in-custody and released inmates
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={toggleAdvancedMode}
          className="flex items-center space-x-2"
        >
          <Icon name={advancedMode ? 'ChevronUp' : 'ChevronDown'} size={16} />
          <span>{advancedMode ? 'Basic' : 'Advanced'}</span>
        </Button>
      </div>

      <form onSubmit={handleSearch} className="space-y-4">
        {/* Basic Fields - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
              Last Name
            </label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              value={searchForm?.lastName}
              onChange={handleInputChange}
              placeholder="e.g., NGUYEN"
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
              First Name
            </label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              value={searchForm?.firstName}
              onChange={handleInputChange}
              placeholder="e.g., DEN VAN"
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="dob" className="block text-sm font-medium text-foreground mb-2">
              Date of Birth
            </label>
            <Input
              id="dob"
              name="dob"
              type="date"
              value={searchForm?.dob}
              onChange={handleInputChange}
              className="w-full"
            />
          </div>

          <Select
            label="Facility"
            placeholder="All Facilities"
            options={[{ value: '', label: 'All Facilities' }, ...facilities]}
            value={searchForm?.facility}
            onChange={(value) => handleSelectChange('facility', value)}
            searchable
            clearable
          />

          <Select
            label="Status"
            placeholder="All Statuses"
            options={statusOptions}
            value={searchForm?.status}
            onChange={(value) => handleSelectChange('status', value)}
          />

          <Select
            label="Agency"
            placeholder="All Agencies"
            options={[{ value: '', label: 'All Agencies' }, ...agencies]}
            value={searchForm?.agency}
            onChange={(value) => handleSelectChange('agency', value)}
            searchable
            clearable
          />
        </div>

        {/* Advanced Fields - Collapsible */}
        {advancedMode && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-md font-medium text-foreground mb-4">Advanced Search Options</h4>
            
            {/* Date Ranges */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-foreground">Arrest Date Range</h5>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    name="arrestDateFrom"
                    type="date"
                    value={searchForm?.arrestDateFrom}
                    onChange={handleInputChange}
                    placeholder="From"
                    className="w-full"
                  />
                  <Input
                    name="arrestDateTo"
                    type="date"
                    value={searchForm?.arrestDateTo}
                    onChange={handleInputChange}
                    placeholder="To"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <h5 className="text-sm font-medium text-foreground">Release Date Range</h5>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    name="releaseDateFrom"
                    type="date"
                    value={searchForm?.releaseDateFrom}
                    onChange={handleInputChange}
                    placeholder="From"
                    className="w-full"
                  />
                  <Input
                    name="releaseDateTo"
                    type="date"
                    value={searchForm?.releaseDateTo}
                    onChange={handleInputChange}
                    placeholder="To"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Additional Search Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label htmlFor="chargeQuery" className="block text-sm font-medium text-foreground mb-2">
                  Charges Search
                </label>
                <Input
                  id="chargeQuery"
                  name="chargeQuery"
                  type="text"
                  value={searchForm?.chargeQuery}
                  onChange={handleInputChange}
                  placeholder="e.g., DUI, Domestic Violence"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="warrantQuery" className="block text-sm font-medium text-foreground mb-2">
                  Warrants Search
                </label>
                <Input
                  id="warrantQuery"
                  name="warrantQuery"
                  type="text"
                  value={searchForm?.warrantQuery}
                  onChange={handleInputChange}
                  placeholder="Search warrant text"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-foreground mb-2">
                  City
                </label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  value={searchForm?.city}
                  onChange={handleInputChange}
                  placeholder="Victim or incident city"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-foreground mb-2">
                  ZIP Code
                </label>
                <Input
                  id="zip"
                  name="zip"
                  type="text"
                  value={searchForm?.zip}
                  onChange={handleInputChange}
                  placeholder="Victim ZIP code"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="bookingNumber" className="block text-sm font-medium text-foreground mb-2">
                  Booking Number
                </label>
                <Input
                  id="bookingNumber"
                  name="bookingNumber"
                  type="text"
                  value={searchForm?.bookingNumber}
                  onChange={handleInputChange}
                  placeholder="Booking/ID number"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="caseNumber" className="block text-sm font-medium text-foreground mb-2">
                  Case Number
                </label>
                <Input
                  id="caseNumber"
                  name="caseNumber"
                  type="text"
                  value={searchForm?.caseNumber}
                  onChange={handleInputChange}
                  placeholder="Case number"
                  className="w-full"
                />
              </div>
            </div>

            {/* Search Options */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="fuzzy"
                  checked={searchForm?.fuzzy}
                  onChange={handleInputChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-foreground">Enable fuzzy search (similarity matching)</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground flex items-center">
            <Icon name="Info" size={16} className="inline mr-2" />
            <span>
              {advancedMode ? 
                'Search will match ANY provided criteria. Use exact date format for best results.' :
                'Use any combination of fields. Click Advanced for more options.'
              }
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <Icon name="RotateCcw" size={16} />
              <span>Clear All</span>
            </Button>

            <Button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <Icon name="Search" size={16} />
              <span>{loading ? 'Searching...' : 'Search Records'}</span>
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FlexibleSearchPanel;